// Angular
import { Router } from '@angular/router';
import { HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

// RxJS
import { BehaviorSubject, Subject } from 'rxjs';

// PrimeNG
import { MessageService } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';

// Environment
import { environment } from 'src/environments/environment';

// Enums
import {
  EOrigenEventoComponent,
  IActionEventCrud,
} from '@/common/enums/general.enum';
import { TypeResponse } from '@/common/enums/general/TypeResponse';

// Abstracts
import { TableGenericAbstract } from '@/abstract/tableGeneric.abstract';

// Services
import { HttpService } from '@/Services/http.service';

// Components
import { ModalGenericComponent } from '@/shared/components/general/modal-generic/modal-generic.component';

// Interfaces
import { IOficinaProductoraForm } from '@/pages/configuraciones/tabla-retencion-documental/oficina-productora/interface/dinamic-form.interface';
import {
  IAccionBoton,
  IDataPagination,
  IPagination,
} from '@/interface/general/IPagination';
import { ITable, TypeColumn } from '@/interface/general/ITable';
import { KeyValueArrayGeneric } from '@/interface/general/params.interface';
import { IResponseApi, IResponseApiSS } from '@/interface/general/IResponseApi';
import { IHerramientaAdministradores } from '@/pages/radicacion/flujo-doc-procesos/interfaces/flujo-doc-procesos.interface';
import { GeneralService } from '@/pages/radicacion/flujo-doc-procesos/renderizar-tabs-dinamicamente/services/general.service';
import { IVariables } from '../../botonera.interface';

export interface IServerSideBackend {
  CodRadicado: string;
  IdfdArchivo: number;
  cmpoCodigoUnico: string;
  ServerSide: TableLazyLoadEvent;
  ExportExcel: boolean;
  ByteFile: boolean;
}

// Interfaces para el modelo de datos
export interface IToolAdmin {
  AyudaxraAdmin: string;
  CodProcedimiento: number;
  Estado: boolean;
  Id: number;
  Nombre: string;
  NombreQuery: string;
  NombreSp: string;
}

@Injectable({
  providedIn: 'root',
})
export class HerramientasParaAdministradoresService extends TableGenericAbstract<IToolAdmin> {
  // Configuración de paginación
  rowsPerPageOptions = environment.paginationLarge;
  rowsPerPage = this.rowsPerPageOptions[0];

  // Subject para notificar al componente que debe refrescar la tabla
  refreshTable$ = new Subject<void>();

  // Cabecera de la tabla
  tableSubject!: ITable;

  // RegistrosSeleccionados
  registrosSeleccionados!: IOficinaProductoraForm[];

  // Subject para los datos de la tabla genérica
  dataTableGenericSubject$ = new BehaviorSubject<
    IDataPagination<IOficinaProductoraForm[]>
  >({
    data: [],
  });

  //Aqui van las url
  // URL del endpoint principal
  private urlApi = 'FlujoDoc/GetToolsAdmin';

  currentPagination!: IPagination;
  data!: IDataPagination<IHerramientaAdministradores[]>;

  //INJECT
  public httpService = inject(HttpService);
  public router = inject(Router);
  private generalService = inject(GeneralService);

  // Dentro del componente padre, NO en el servicio
  constructor(
    public config: DynamicDialogConfig<IVariables>,
    private serviceMensaje: MessageService,
    private dialogService: DialogService
  ) {
    super();
  }

  //#region loadConfig

  async onLoadRecords(event: any, origen = EOrigenEventoComponent.BTN_TABLE) {
    let serverSide: TableLazyLoadEvent;

    // 1 = filtro, 2 = table
    if (origen === EOrigenEventoComponent.BTN_FILTER) {
      /** Este objeto no se modifica */
      serverSide = {
        first: 0,
        rows: this.rowsPerPage,
        sortField: '',
        sortOrder: 0,
        filters: {},
        globalFilter: null,
      };
      /****************************** */
    } else {
      // Aqui se agregan los parametros que vienen del Filtro
      serverSide = event;
    }

    // ESTA CONSTANTE NO SE MODIFICA
    const paramsObject: IServerSideBackend = {
      CodRadicado: this.config?.data?.CodRadicado ?? '0',
      IdfdArchivo: this.config?.data?.IdfdArchivo ?? 0,
      cmpoCodigoUnico: this.config?.data?.cmpoCodigoUnico ?? '0',
      ServerSide: serverSide,
      ExportExcel: false,
      ByteFile: false,
    };

    const httpParams = this.dynamicParams(paramsObject);

    this.callHttpService(httpParams);
  }


  override async callHttpService(httpParams: HttpParams) {
    try {
      this.httpService.GetParams(this.urlApi, httpParams).subscribe({
        next: (resp: IResponseApiSS) => {
          const { data, metadataServerSide } = resp;

          // OJo no se modifica
          const datosTablaGenericConsultada: IDataPagination<
            IOficinaProductoraForm[]
          > = {
            data: data,
            pagination: {
              first:
                metadataServerSide?.numeroPagina *
                metadataServerSide.filasxPagina,
              rows: metadataServerSide.filasxPagina,
            },
            totalRecords: metadataServerSide?.totalRegitros,
          };
          this.totalRecords = datosTablaGenericConsultada?.totalRecords ?? 0;
          this.dataTableGenericSubject$.next(datosTablaGenericConsultada);
        }
      });
    } catch (error) {
    }
  }
  //#endregion

  //#region Parámetros Dinámicos
  dynamicParams(paramsObject: object): HttpParams {
    let params = new HttpParams();

    Object.entries(paramsObject).forEach(([key, value]) => {
      params = params.append(key,(typeof value === 'object' && value !== null) ? JSON.stringify(value) : value);
    });

    return params;
  }

  //#endregion

  //#region Exportación
  
  onSelected(registrosSeleccionados: IOficinaProductoraForm[]) {
    this.registrosSeleccionados = registrosSeleccionados;
  }

  //#endregion

  override setHeaderTable(): void {
    const table: ITable = this.getTableConfig();
    this.tableSubject = table;
  }

  override getTableConfig(): ITable {
    return {
      fieldId: 'id',
      paginator: true,
      rowsPerPageOptions: [5, 10, 15],
      lazyLoadOnInit: true,
      columns: [
        {
          field: 'Id',
          label: 'Id',
          type: TypeColumn.NUMBER,
          filter: false,
          sort: false,
        },
        {
          field: 'NombreQuery',
          label: 'Nombre',
          type: TypeColumn.STRING,
          filter: true,
          sort: false,
        },
        {
          field: 'AyudaxraAdmin',
          label: 'Descripción',
          type: TypeColumn.STRING,
          filter: true,
          sort: false,
        },
        {
          field: 'acciones',
          label: 'Acciones',
          type: TypeColumn.ACTION,
          showActions: false,
          showSelector: false,
          action: (rowData: IToolAdmin, btn: IAccionBoton) =>
            this.handleAction(rowData, btn),
        },
      ],
    };
  }

  override onExportSelectedExcel(): void {
    throw new Error('Method not implemented.');
  }

  override exportarExcel(httpParams: HttpParams): void {
    throw new Error('Method not implemented.' + httpParams);
  }

  override handleAction(record: IToolAdmin, btn: IAccionBoton): void {
    const ref = this.dialogService.open(ModalGenericComponent, {
      header: '▶️ Ejecutar acción!',
      width: '350px',
      modal: true,
      closable: true,
      maximizable: false,
      dismissableMask: true,
      contentStyle: {
        'border-radius': '8px',
        'box-shadow': '0 10px 30px rgba(51, 27, 27, 0.2)',
      },
      data: {
        title: '¡Atención!',
        message: `¿Desea ejecutar el flujo con id ${record.Id}?`,
        tipoConfirmacion: 'warning',
        confirmLabel: 'Sí, ejecutar',
        cancelLabel: 'Cancelar',
      },
    });

    ref.onClose.subscribe((result) => {
      if (result) {
        const CodRadicado = this.config?.data?.CodRadicado ?? '0';
        const IdfdArchivo = Number(this.config?.data?.IdfdArchivo ?? 0);
        const cmpoCodigoUnico = this.config?.data?.cmpoCodigoUnico ?? '0';

        const params: KeyValueArrayGeneric<string | number> = [
          { key: 'Procedimiento', value: record.CodProcedimiento.toString() },
          { key: 'NombreQuery', value: record.NombreQuery.toString() },
          { key: 'CodRadicado', value: CodRadicado },
          { key: 'IdfdArchivo', value: IdfdArchivo },
          { key: 'cmpoCodigoUnico', value: cmpoCodigoUnico },
        ];

        this.generalService
          .getWitchParamsAndUrl(params, 'FlujoDoc/ExecToolsAdmin')
          .subscribe({
            next: (response: IResponseApi) => {
              if (response.status === TypeResponse.OK) {
                // Notificación de éxito
                this.serviceMensaje.add({
                  severity: 'info',
                  summary: 'Proceso ejecutado',
                  detail: response.mensaje || 'Proceso ejecutado correctamente',
                });
              }
            }
          });
      }
    });
  }

  override newRegister(): void {
    throw new Error('Method not implemented.');
  }

  override descargarPdf(record: IToolAdmin): void {
    throw new Error('Method not implemented.' + record);
  }

  override procesarPdf(record: IToolAdmin): void {
    throw new Error('Method not implemented.' + record);
  }
}
